
import React, { PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './BillboardNoSignInBoxVariant.css';
import { transformHTMLParserLinks } from '../../../utils/rewriteLink/rewriteLink';
import { useWidthContext } from '../../../providers/Width';
import { SideMenuContext } from '../../../providers/SideMenuProvider/SideMenuProvider';
import { Icon } from '../../../types/shared/Icon';
import axios from 'axios';
import { getMarketingAttributes } from '../../../utils/acquisition-utils';
import { Row, Col, Button, Section, Header, Title } from '../../../importCL';


const DEFAULT_DESKTOP_IMAGE_WIDTH = 389;
const DEFAULT_DESKTOP_IMAGE_HEIGHT = 245;
const DEFAULT_MOBILE_IMAGE_WIDTH = 300;
const DEFAULT_MOBILE_IMAGE_HEIGHT = 189;

interface IMainNavigationItem {
    linkTitle: string;
    link: string;
    linkParam: string;
    linkNewTab: boolean;
}

export type TCreateSideMenu = {
    title: any;
    link?: string;
    linkParams?: string;
    newTab?: boolean;
    next?: TCreateSideMenu | TCreateSideMenu[];
};

export interface IBillboardNoSignInBoxVariantProps extends MappedComponentProperties {
    author?: boolean;

    className?: string;
    signInTitle?: string;

    ctaLinkLabel?: string;
    ctaLink?: string;
    ctaNewTab?: boolean;

    mainText?: string;
    subText?: string;

    bgColor?: string;
    hexColor?: string;

    tertiaryTextDesktop?: string;
    tertiaryTextMobile?: string;

    mainImgPath?: string;
    mainMobileImgPath?: string;
    overwriteMobileStyle?: string;
    usingOverwriteMobileStyle?: boolean;

    mainImgAltText?: string;
    overlayImgPath?: string;
    preLoadDesktopBkgTopImage?: boolean;
    bkgTopImgPath?: string;
    mobileBkgTopImgPath?: string;

    validationErrorCode?: string;
    loginErrorCode?: string;
    genericProcessingErrorCode?: string;
    suspendedAccountFraudErrorCode?: string;
    suspendedAccountGeneralErrorCode?: string;
    failedDeviceCheckErrorCode?: string;

    navBarLinks?: IMainNavigationItem[];
    showHamburgerButton?: boolean;
    hideCtaBtnDesktop?: boolean;

    headerCtaLabel?: string;
    headerCtaLink?: string;
    headerCtaModal?: boolean;
    headerCtaNewTab?: boolean;
    ctaButtonVariant?: 'solid' | 'ghosted-light';
    showHeaderCta?: boolean;
    headerCtaIcon?: 'lock' | 'chevron' | 'back';

    signInCtaLabel?: string;

    container?: TCreateSideMenu;
    homeLabel?: string;
    homeLink?: string;

    forgotUserNameLink?: string;
    forgotPasswordLink?: string;
    setupAccountLink?: string;

    smsIcon?: Icon;
    emailIcon?: Icon;
    callIcon?: Icon;

    preloadDesktopImage?: boolean;

    removeHeader?: boolean;
    removeSignInBox?: boolean;
}

export const BillboardNoSignInBoxVariantEditConfig = {
    emptyLabel: 'BillboardNoSignInBoxVariant',
    isEmpty: (props: PropsWithChildren<IBillboardNoSignInBoxVariantProps>) => {
        return !props || !props.mainText;
    },
};

const sendMarketingData = () => {
    const marketingData = getMarketingAttributes();
    // Developer's Note ::: DPM-8327
    //  /api/v1/marketingdata expects the data in {Key: string, Value: string} format
    //    whereas our acquisitionsapi expects the data in {Name: string, Value: string} format.
    if (marketingData && Object.keys(marketingData).length > 0) {
        const reformattedMarketingData = Object.entries(marketingData).map(([Key, Value]) => ({ Key, Value }));
        axios.post('/api/v1/marketingdata', reformattedMarketingData)
            .then(() => {
                // eslint-disable-next-line no-console
                console.debug('Marketing Data API Set/Called');
            })
            .catch(() => {
                console.error('Error Has Occurred With Marketing Data');
            });
    }
};

const BillboardNoSignInBoxVariant = (props: PropsWithChildren<IBillboardNoSignInBoxVariantProps>): JSX.Element => {
    const { hamburgerOpen, setHamburgerOpen, hamburgerSide, setHamburgerContainer } = useContext(SideMenuContext);
    const { width } = useWidthContext();
    const { mainText = '', subText = '' } = props;
    const [isOpen, setOpen] = useState(false);
    const { className = '',
        tertiaryTextDesktop = '',
        tertiaryTextMobile = '',
        homeLabel = 'Home',
        homeLink = '/',
    } = props;
    const isMobile = width <= 768;
    const tertiaryText = isMobile ? tertiaryTextMobile : tertiaryTextDesktop;
    const imgPath = isMobile && props.mainMobileImgPath ? props.mainMobileImgPath : props.mainImgPath;
    const [ styleOverride, setStyleOverride ] = useState<React.CSSProperties>(undefined);
    const imageWidth = isMobile ? DEFAULT_MOBILE_IMAGE_WIDTH : DEFAULT_DESKTOP_IMAGE_WIDTH;
    const imageHeight = isMobile ? DEFAULT_MOBILE_IMAGE_HEIGHT : DEFAULT_DESKTOP_IMAGE_HEIGHT;

    const getNavBarLinks = useCallback((map: IMainNavigationItem[] | undefined) => {
        const navBarLinks: { label: any; path: string; param: string; newTab: boolean; }[] = [];
        if (map !== undefined) {
            map.forEach(item => {
                const obj = {
                    label: item.linkTitle,
                    path: item.link,
                    param: item.linkParam,
                    newTab: item.linkNewTab,
                };
                navBarLinks.push(obj);
            });
        }
        return navBarLinks;
    }, []);

    const getCTAParams = useCallback(() => {
        if (!props.headerCtaLabel) {
            return;
        }

        const obj = {
            label: props.headerCtaLabel,
            newTab: props.headerCtaNewTab,
            icon: props.headerCtaIcon,
            path: !props.headerCtaModal ? props.headerCtaLink : undefined,
            callback: props.headerCtaModal ? () => setOpen(!isOpen) : undefined,
        };
        return obj;

    }, []);

    useEffect(() => {
        if (props.author) {
            return;
        }
        
        const timeoutId = setTimeout(() => {
            sendMarketingData();
        }, 0);

        return () => {
            clearTimeout(timeoutId);
        };
    }, []);

    useEffect(() => {
        if (!props.usingOverwriteMobileStyle || !props.overwriteMobileStyle) {
            setStyleOverride(undefined);
            return;
        }

        try {
            setStyleOverride(JSON.parse(props.overwriteMobileStyle));
        } catch (exception) {
            console.error('Invalid JSON in Overwrite Mobile Style : ' + exception);
        }
    }, [props]);

    useEffect(() => {
        if (!props.container) {
            return;
        }

        const obj = {
            'title': transformHTMLParserLinks(homeLabel),
            'link': homeLink,
            'next': props.container,
        };

        if (!props.removeHeader) {
            setHamburgerContainer(obj);
        }
    }, [props.container, homeLabel, homeLink, setHamburgerContainer]);

    return (
        <div id='BillboardNoSignInBoxVariant' className={`BillboardNoSignInBoxVariant ${className}`}>
            {props.preLoadDesktopBkgTopImage && props.bkgTopImgPath &&
                <img className='hidden' src={props.bkgTopImgPath} />
            }
            <Section className={`BillboardNoSignInBoxVariant__section${props.removeHeader ? '--hiddenHeader': ''}`} containerClass='BillboardNoSignInBoxVariant__container' bgImg={isMobile ? props.mobileBkgTopImgPath : props.bkgTopImgPath}>
                {!props.removeHeader && <Header
                    className='BillboardNoSignInBoxVariant__header'
                    theme='transparent'
                    navBarLinks={getNavBarLinks(props.navBarLinks)}
                    hideNavButtonsOnDesktop={props.hideCtaBtnDesktop}
                    showCTAButton={props.showHeaderCta}
                    CTAParams={getCTAParams()}
                    hamburger={props.showHamburgerButton}
                    hamburgerSide={hamburgerSide}
                    isHamburgerOpen={hamburgerOpen}
                    hamburgerCallBack={() => setHamburgerOpen(!hamburgerOpen)}
                />}
                <Row className='BillboardNoSignInBoxVariant__row' align='start'>
                    <Col lg={5} className='BillboardNoSignInBoxVariant__heroContainer COL-6'>
                        <Title data-testid='mainText'
                            align='left'
                            className='BillboardNoSignInBoxVariant__mainText'>
                            <span>{transformHTMLParserLinks(mainText)}</span>
                        </Title>
                        <p className='BillboardNoSignInBoxVariant__subText'>
                            {transformHTMLParserLinks(subText)}
                        </p>
                        {props.ctaLinkLabel && props.ctaLink && <Button className={`BillboardNoSignInBoxVariant__heroButton ${props.ctaButtonVariant === 'solid' ? 'BillboardNoSignInBoxVariant__heroButton--whiteOutlineVariant' : ''}`} size='large' variant={props.ctaButtonVariant} href={props.ctaLink} target={props.ctaNewTab ? '_blank' : '_self'}>
                            {transformHTMLParserLinks(props.ctaLinkLabel) as any}
                        </Button>}
                        {tertiaryText && <span className='BillboardNoSignInBoxVariant__tertiaryText'>{transformHTMLParserLinks(tertiaryText)}</span>}
                    </Col>
                    <Col className='BillboardNoSignInBoxVariant__imgContainer'>

                        {props.preloadDesktopImage && props.mainImgPath && <img fetchpriority='high' className='hidden' src={props.mainImgPath} />}
                        <img fetchpriority='high' className={`BillboardNoSignInBoxVariant__mainImg${isMobile ? '--mobile' : '--desktop'}`}
                            id={`BillboardNoSignInBoxVariant__mainImg${isMobile ? '--mobile' : '--desktop'}`}
                            alt={props.mainImgAltText}
                            src={imgPath}
                            width={imageWidth}
                            height={imageHeight}
                            style={isMobile ? styleOverride : undefined }/>
                    </Col>
                </Row>
            </Section>
        </div>
    );
};
export default BillboardNoSignInBoxVariant;
